import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { HiOutlinePlus, HiOutlineTrash, HiOutlineX } from "react-icons/hi";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import DateTimePicker from "../../components/DateTimePicker";
import FilePreviewModal, { detectPreviewKind } from "components/FilePreviewModal";
import type { FilePreviewKind } from "components/FilePreviewModal";
import uploadDropzoneGraphic from "assets/svg/upload-modal-dropzone-graphic.svg";

import {
  generateOppositionStatusDetailsPresignedUrls,
  getOppositionStatusDetails,
  saveOppositionStatusDetails,
  OPPOSITION_ADDRESSEE_DESIGNATIONS,
  OPPOSITION_APPLICANT_TYPE_OPTIONS,
  OPPOSITION_HONORIFIC_OPTIONS,
  OPPOSITION_TYPE_OF_MARK_OPTIONS,
  OPPOSITION_CLASS_NUMBERS,
  OPPOSITION_KERALA_DISTRICTS,
  type OppositionConflictingMarkPayload,
} from "../../api/opposition";

export const OPPOSITION_STATUS_DETAILS_FORM_ID = "opposition-status-details-form";

const MAX_FILE_SIZE = 50 * 1024 * 1024;
const LABEL_CLASS = "text-[12px] font-normal leading-5 text-[#222222]";
const INPUT_CLASS =
  "w-full min-h-[40px] md:h-9 md:min-h-0 px-2.5 py-2 text-[12px] leading-5 text-[#222222] placeholder:text-[#6D6D6D] bg-white border border-[#EEEEEE] rounded-[6px] focus:outline-none focus:ring-1 focus:ring-[#222] box-border";
const TEXTAREA_CLASS =
  "w-full min-h-[64px] px-2.5 py-2 text-[12px] leading-5 text-[#222222] placeholder:text-[#6D6D6D] bg-white border border-[#EEEEEE] rounded-[6px] focus:outline-none focus:ring-1 focus:ring-[#222] box-border resize-y";
const REQ = <span className="text-red-500 ml-0.5">*</span>;

let rowCounter = 0;
function makeRowId(): string {
  rowCounter += 1;
  return `cm-${rowCounter}-${Date.now()}`;
}

function parseDateStr(s: string | null | undefined): Date | null {
  if (!s) return null;
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) {
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}

function toISODate(d: Date | null): string | null {
  if (!d) return null;
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${mo}-${day}`;
}

function addTwoMonths(d: Date): Date {
  const result = new Date(d);
  result.setMonth(result.getMonth() + 2);
  return result;
}

type ConflictingMarkRow = {
  rowId: string;
  savedId: string | null;
  nameOfProprietor: string;
  addressOfProprietor: string;
  placeOfProprietor: string;
  conflictingMark: string;
  applicationNo: string;
  classOfMark: string;
  goodsServices: string;
  userDate: Date | null;
  journalDate: Date | null;
  lastDateOfFiling: Date | null;
  savedImageFileKey: string | null;
  pendingImageFile: File | null;
  localImagePreviewUrl: string | null;
  savedStatusFileKey: string | null;
  savedStatusFilename: string | null;
  pendingStatusFile: File | null;
};

function makeEmptyRow(): ConflictingMarkRow {
  return {
    rowId: makeRowId(),
    savedId: null,
    nameOfProprietor: "",
    addressOfProprietor: "",
    placeOfProprietor: "",
    conflictingMark: "",
    applicationNo: "",
    classOfMark: "",
    goodsServices: "",
    userDate: null,
    journalDate: null,
    lastDateOfFiling: null,
    savedImageFileKey: null,
    pendingImageFile: null,
    localImagePreviewUrl: null,
    savedStatusFileKey: null,
    savedStatusFilename: null,
    pendingStatusFile: null,
  };
}

type Props = {
  leadId?: string | null;
  onSuccess?: (leadId: string) => void;
};

export default function OppositionStatusDetailsView({ leadId, onSuccess }: Props) {
  const [loading, setLoading] = useState<boolean>(!!leadId);
  const [saving, setSaving] = useState(false);
  const loadedRef = useRef(false);

  const [errors, setErrors] = useState<Set<string>>(new Set());
  const hasError = (key: string) => errors.has(key);
  const eb = (key: string) => (errors.has(key) ? " !border-red-500" : "");
  const clearError = (key: string) =>
    setErrors((prev) => {
      if (!prev.has(key)) return prev;
      const next = new Set(prev);
      next.delete(key);
      return next;
    });

  // Our Mark fields
  const [mark, setMark] = useState(""); // read-only display
  const [tmNumber, setTmNumber] = useState("");
  const [selectedClass, setSelectedClass] = useState("");
  const [typeOfMark, setTypeOfMark] = useState("");
  const [goodsServices, setGoodsServices] = useState("");
  const [savedImageOfMarkKey, setSavedImageOfMarkKey] = useState<string | null>(null);
  const [pendingImageOfMarkFile, setPendingImageOfMarkFile] = useState<File | null>(null);
  const [localImageOfMarkUrl, setLocalImageOfMarkUrl] = useState<string | null>(null);

  // Applicant fields
  const [applicant, setApplicant] = useState("");
  const [applicantAddressLine, setApplicantAddressLine] = useState("");
  const [applicantAddressDistrict, setApplicantAddressDistrict] = useState("");
  const [applicantAddressState, setApplicantAddressState] = useState("Kerala");
  const [applicantAddressCountry, setApplicantAddressCountry] = useState("India");
  const [applicantType, setApplicantType] = useState("");
  const [applicantTypeOther, setApplicantTypeOther] = useState("");
  const [signatoryName, setSignatoryName] = useState("");
  const [signatoryDesignation, setSignatoryDesignation] = useState("");
  const [userDate, setUserDate] = useState<Date | null>(null);
  const [dateOfFiling, setDateOfFiling] = useState<Date | null>(null);

  // Addressee fields
  const [addresseeDesignation, setAddresseeDesignation] = useState("");
  const [addresseeCustomName, setAddresseeCustomName] = useState("");
  const [firmName, setFirmName] = useState("");
  const [place, setPlace] = useState("");
  const [addresseeHonorific, setAddresseeHonorific] = useState("");

  // Conflicting marks
  const [conflictingMarks, setConflictingMarks] = useState<ConflictingMarkRow[]>([makeEmptyRow()]);

  // Preview modal
  const [preview, setPreview] = useState<{
    url: string;
    name: string;
    kind: FilePreviewKind;
    revokeOnClose?: boolean;
  } | null>(null);

  useEffect(() => {
    if (!leadId || loadedRef.current) return;
    loadedRef.current = true;
    let cancelled = false;
    setLoading(true);

    getOppositionStatusDetails(leadId)
      .then((data) => {
        if (cancelled) return;
        setMark(data.mark ?? "");
        setTmNumber(data.tmNumber ?? "");
        setSelectedClass(data.selectedClass != null ? String(data.selectedClass) : "");
        setTypeOfMark(data.typeOfMark ?? "");
        setGoodsServices(data.goodsServices ?? "");
        setSavedImageOfMarkKey(data.imageOfMarkKey ?? null);

        setApplicant(data.applicant ?? "");
        setApplicantAddressLine(data.applicantAddressLine ?? "");
        setApplicantAddressDistrict(data.applicantAddressDistrict ?? "");
        setApplicantAddressState(data.applicantAddressState ?? "Kerala");
        setApplicantAddressCountry(data.applicantAddressCountry ?? "India");
        setApplicantType(data.applicantType ?? "");
        setApplicantTypeOther(data.applicantTypeOther ?? "");
        setSignatoryName(data.signatoryName ?? "");
        setSignatoryDesignation(data.signatoryDesignation ?? "");
        setUserDate(parseDateStr(data.userDate));
        setDateOfFiling(parseDateStr(data.dateOfFiling));

        setAddresseeDesignation(data.addresseeDesignation ?? "");
        setAddresseeCustomName(data.addresseeCustomName ?? "");
        setFirmName(data.firmName ?? "");
        setPlace(data.place ?? "");
        setAddresseeHonorific(data.addresseeHonorific ?? "");

        if (data.conflictingMarks.length > 0) {
          setConflictingMarks(
            data.conflictingMarks.map((cm) => {
              const jd = parseDateStr(cm.journalDate);
              const lastDate =
                parseDateStr(cm.lastDateOfFilingOpposition) ??
                (jd ? addTwoMonths(jd) : null);
              return {
                rowId: makeRowId(),
                savedId: cm.id,
                nameOfProprietor: cm.nameOfProprietor ?? "",
                addressOfProprietor: cm.addressOfProprietor ?? "",
                placeOfProprietor: cm.placeOfProprietor ?? "",
                conflictingMark: cm.conflictingMark ?? "",
                applicationNo: cm.applicationNo ?? "",
                classOfMark: cm.classOfMark != null ? String(cm.classOfMark) : "",
                goodsServices: cm.goodsServices ?? "",
                userDate: parseDateStr(cm.userDate),
                journalDate: jd,
                lastDateOfFiling: lastDate,
                savedImageFileKey: cm.imageFileKey ?? null,
                pendingImageFile: null,
                localImagePreviewUrl: null,
                savedStatusFileKey: cm.statusOfConflictingMarkFileKey ?? null,
                savedStatusFilename: cm.statusOfConflictingMarkFilename ?? null,
                pendingStatusFile: null,
              };
            })
          );
        }
      })
      .catch((err) => {
        if (cancelled) return;
        toast.error(err instanceof Error ? err.message : "Failed to load status details");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [leadId]);

  useEffect(() => {
    return () => {
      if (localImageOfMarkUrl) URL.revokeObjectURL(localImageOfMarkUrl);
      conflictingMarks.forEach((row) => {
        if (row.localImagePreviewUrl) URL.revokeObjectURL(row.localImagePreviewUrl);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateRow = (rowId: string, updates: Partial<ConflictingMarkRow>) => {
    setConflictingMarks((prev) =>
      prev.map((row) => (row.rowId === rowId ? { ...row, ...updates } : row))
    );
  };

  const addConflictingMark = () => {
    setConflictingMarks((prev) => [...prev, makeEmptyRow()]);
  };

  const removeConflictingMark = (rowId: string) => {
    setConflictingMarks((prev) => {
      if (prev.length <= 1) {
        toast.error("At least one conflicting mark is required");
        return prev;
      }
      const row = prev.find((r) => r.rowId === rowId);
      if (row?.localImagePreviewUrl) URL.revokeObjectURL(row.localImagePreviewUrl);
      return prev.filter((r) => r.rowId !== rowId);
    });
  };

  const handleImageOfMarkFile = (file: File) => {
    if (file.size > MAX_FILE_SIZE) { toast.error(`${file.name} exceeds the 50MB limit`); return; }
    if (localImageOfMarkUrl) URL.revokeObjectURL(localImageOfMarkUrl);
    setPendingImageOfMarkFile(file);
    setLocalImageOfMarkUrl(URL.createObjectURL(file));
  };

  const handleConflictingMarkImageFile = (rowId: string, file: File) => {
    if (file.size > MAX_FILE_SIZE) { toast.error(`${file.name} exceeds the 50MB limit`); return; }
    setConflictingMarks((prev) =>
      prev.map((row) => {
        if (row.rowId !== rowId) return row;
        if (row.localImagePreviewUrl) URL.revokeObjectURL(row.localImagePreviewUrl);
        return { ...row, pendingImageFile: file, localImagePreviewUrl: URL.createObjectURL(file) };
      })
    );
    clearError(`${rowId}:image`);
  };

  const handleConflictingMarkStatusPdf = (rowId: string, file: File) => {
    if (file.size > MAX_FILE_SIZE) { toast.error(`${file.name} exceeds the 50MB limit`); return; }
    updateRow(rowId, { pendingStatusFile: file });
    clearError(`${rowId}:statusOfConflictingMark`);
  };

  const validate = (marks: ConflictingMarkRow[]): Set<string> => {
    const next = new Set<string>();

    // Our Mark Details
    if (!selectedClass) next.add("selectedClass");
    if (!typeOfMark) next.add("typeOfMark");
    if (!goodsServices.trim()) next.add("goodsServices");

    // Applicant Details
    if (!applicant.trim()) next.add("applicant");
    if (!applicantAddressLine.trim()) next.add("applicantAddressLine");
    if (!applicantAddressDistrict) next.add("applicantAddressDistrict");
    if (!applicantAddressState.trim()) next.add("applicantAddressState");
    if (!applicantAddressCountry.trim()) next.add("applicantAddressCountry");
    if (!applicantType) next.add("applicantType");
    if (applicantType === "OTHERS" && !applicantTypeOther.trim()) next.add("applicantTypeOther");
    if (!signatoryName.trim()) next.add("signatoryName");
    if (!signatoryDesignation.trim()) next.add("signatoryDesignation");
    if (!userDate) next.add("userDate");
    if (!dateOfFiling) next.add("dateOfFiling");

    // Addressee
    if (!addresseeDesignation) next.add("addresseeDesignation");
    if (addresseeDesignation === "NAME" && !addresseeCustomName.trim()) next.add("addresseeCustomName");
    if (!firmName.trim()) next.add("firmName");
    if (!place.trim()) next.add("place");
    if (!addresseeHonorific) next.add("addresseeHonorific");

    // Per conflicting mark
    for (const row of marks) {
      if (!row.nameOfProprietor.trim()) next.add(`${row.rowId}:nameOfProprietor`);
      if (!row.addressOfProprietor.trim()) next.add(`${row.rowId}:addressOfProprietor`);
      if (!row.placeOfProprietor.trim()) next.add(`${row.rowId}:placeOfProprietor`);
      if (!row.conflictingMark.trim()) next.add(`${row.rowId}:conflictingMark`);
      if (!row.applicationNo.trim()) next.add(`${row.rowId}:applicationNo`);
      if (!row.classOfMark) next.add(`${row.rowId}:classOfMark`);
      if (!row.goodsServices.trim()) next.add(`${row.rowId}:goodsServices`);
      if (!row.userDate) next.add(`${row.rowId}:userDate`);
      if (!row.journalDate) next.add(`${row.rowId}:journalDate`);
      if (!row.lastDateOfFiling) next.add(`${row.rowId}:lastDateOfFiling`);
      if (!row.pendingStatusFile && !row.savedStatusFileKey) {
        next.add(`${row.rowId}:statusOfConflictingMark`);
      }
    }

    return next;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || saving) return;

    const validationErrors = validate(conflictingMarks);
    if (validationErrors.size > 0) {
      setErrors(validationErrors);
      toast.error("Please fill in all required fields.");
      return;
    }

    setSaving(true);
    try {
      type PendingUpload = { file: File; tag: string };
      const uploads: PendingUpload[] = [];
      const tagImageOfMark = "imageOfMark";
      if (pendingImageOfMarkFile) uploads.push({ file: pendingImageOfMarkFile, tag: tagImageOfMark });
      conflictingMarks.forEach((row) => {
        if (row.pendingImageFile) uploads.push({ file: row.pendingImageFile, tag: `img:${row.rowId}` });
        if (row.pendingStatusFile) uploads.push({ file: row.pendingStatusFile, tag: `pdf:${row.rowId}` });
      });

      let keyMap: Record<string, string> = {};
      if (uploads.length > 0) {
        const presigned = await generateOppositionStatusDetailsPresignedUrls(
          leadId,
          uploads.map((u) => ({ filename: u.file.name, mimeType: u.file.type || "application/octet-stream" }))
        );
        if (presigned.length !== uploads.length) throw new Error("Failed to generate upload URLs");
        await Promise.all(
          uploads.map(async (u, i) => {
            const target = presigned[i];
            const res = await fetch(target.url, {
              method: "PUT",
              body: u.file,
              headers: { "Content-Type": u.file.type || "application/octet-stream" },
            });
            if (!res.ok) throw new Error(`Failed to upload ${u.file.name}`);
            keyMap[u.tag] = target.key;
          })
        );
      }

      const resolvedImageOfMarkKey = keyMap[tagImageOfMark] ?? savedImageOfMarkKey ?? null;

      const conflictingMarksPayload: OppositionConflictingMarkPayload[] = conflictingMarks.map((row) => {
        const resolvedImageFileKey = keyMap[`img:${row.rowId}`] ?? row.savedImageFileKey ?? undefined;
        const resolvedStatusKey = keyMap[`pdf:${row.rowId}`] ?? row.savedStatusFileKey ?? undefined;
        const resolvedStatusFilename = keyMap[`pdf:${row.rowId}`]
          ? row.pendingStatusFile!.name
          : row.savedStatusFilename ?? undefined;

        const p: OppositionConflictingMarkPayload = {};
        if (row.nameOfProprietor) p.nameOfProprietor = row.nameOfProprietor;
        if (row.addressOfProprietor) p.addressOfProprietor = row.addressOfProprietor;
        if (row.placeOfProprietor) p.placeOfProprietor = row.placeOfProprietor;
        if (row.conflictingMark) p.conflictingMark = row.conflictingMark;
        if (row.applicationNo) p.applicationNo = row.applicationNo;
        if (resolvedImageFileKey) p.imageFileKey = resolvedImageFileKey;
        if (row.classOfMark) p.classOfMark = Number(row.classOfMark);
        if (row.goodsServices) p.goodsServices = row.goodsServices;
        const ud = toISODate(row.userDate); if (ud) p.userDate = ud;
        const jd = toISODate(row.journalDate); if (jd) p.journalDate = jd;
        const ld = toISODate(row.lastDateOfFiling); if (ld) p.lastDateOfFilingOpposition = ld;
        if (resolvedStatusKey) p.statusOfConflictingMarkFileKey = resolvedStatusKey;
        if (resolvedStatusFilename) p.statusOfConflictingMarkFilename = resolvedStatusFilename;
        return p;
      });

      const saved = await saveOppositionStatusDetails(leadId, {
        mark: mark || undefined,
        tmNumber: tmNumber.trim() || null,
        selectedClass: selectedClass ? Number(selectedClass) : undefined,
        typeOfMark: typeOfMark || null,
        goodsServices: goodsServices || null,
        imageOfMarkKey: resolvedImageOfMarkKey,
        applicant: applicant || null,
        applicantAddressLine: applicantAddressLine || null,
        applicantAddressDistrict: applicantAddressDistrict || null,
        applicantAddressState: applicantAddressState || null,
        applicantAddressCountry: applicantAddressCountry || null,
        applicantType: applicantType || null,
        applicantTypeOther: applicantType === "OTHERS" ? applicantTypeOther || null : null,
        signatoryName: signatoryName || null,
        signatoryDesignation: signatoryDesignation || null,
        userDate: toISODate(userDate),
        dateOfFiling: toISODate(dateOfFiling),
        addresseeDesignation: addresseeDesignation || null,
        addresseeCustomName: addresseeDesignation === "NAME" ? addresseeCustomName || null : null,
        firmName: firmName || null,
        place: place || null,
        addresseeHonorific: addresseeHonorific || null,
        conflictingMarks: conflictingMarksPayload,
      });

      setSavedImageOfMarkKey(saved.imageOfMarkKey ?? null);
      setPendingImageOfMarkFile(null);
      if (localImageOfMarkUrl) { URL.revokeObjectURL(localImageOfMarkUrl); setLocalImageOfMarkUrl(null); }

      setConflictingMarks((prev) =>
        prev.map((row, idx) => {
          const serverRow = saved.conflictingMarks[idx];
          if (!serverRow) return row;
          return {
            ...row,
            savedId: serverRow.id,
            savedImageFileKey: serverRow.imageFileKey ?? row.savedImageFileKey,
            pendingImageFile: null,
            savedStatusFileKey: serverRow.statusOfConflictingMarkFileKey ?? row.savedStatusFileKey,
            savedStatusFilename: serverRow.statusOfConflictingMarkFilename ?? row.savedStatusFilename,
            pendingStatusFile: null,
          };
        })
      );

      setErrors(new Set());
      toast.success("Status details saved successfully.");
      onSuccess?.(leadId);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save status details");
    } finally {
      setSaving(false);
    }
  };

  if (!leadId) {
    return (
      <div className="flex flex-col gap-[22px] w-full max-w-[1085px] py-12" style={{ fontFamily: "Inter, sans-serif" }}>
        <p className="text-sm text-[#6D6D6D]">Please complete the previous steps to access this step.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex flex-col gap-[22px] w-full max-w-[1085px] py-12" style={{ fontFamily: "Inter, sans-serif" }}>
        <p className="text-sm text-[#6D6D6D]">Loading...</p>
      </div>
    );
  }

  return (
    <form
      id={OPPOSITION_STATUS_DETAILS_FORM_ID}
      onSubmit={handleSubmit}
      className="flex flex-col gap-5 w-full max-w-[1085px]"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {/* Our Mark Details */}
      <div className="flex flex-col gap-5 bg-white border border-[#EEEEEE] rounded-[10px] p-5">
        <p className="text-[14px] font-semibold text-[#101111]">Our Mark Details</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="flex flex-col gap-1">
            <label className={LABEL_CLASS}>Mark</label>
            <div className={INPUT_CLASS + " cursor-default select-text"}>
              {mark || <span className="text-[#6D6D6D]">—</span>}
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <label className={LABEL_CLASS}>TM Number</label>
            <input
              type="text"
              value={tmNumber}
              onChange={(e) => setTmNumber(e.target.value)}
              placeholder="Enter TM Number (optional)"
              className={INPUT_CLASS}
              maxLength={100}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className={LABEL_CLASS}>Selected Class{REQ}</label>
            <Select
              value={selectedClass}
              onValueChange={(v) => { setSelectedClass(v); clearError("selectedClass"); }}
            >
              <SelectTrigger className={INPUT_CLASS + eb("selectedClass")}>
                <SelectValue placeholder="Select Class" />
              </SelectTrigger>
              <SelectContent>
                {OPPOSITION_CLASS_NUMBERS.map((n) => (
                  <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1.5">
            <span className={LABEL_CLASS}>Type of Mark{REQ}</span>
            <div className={`flex flex-wrap gap-x-5 gap-y-2 rounded-[6px] ${hasError("typeOfMark") ? "outline outline-1 outline-red-500 p-1" : ""}`}>
              {OPPOSITION_TYPE_OF_MARK_OPTIONS.map((opt) => (
                <label
                  key={opt}
                  className="flex cursor-pointer items-center gap-2 text-[12px] font-normal leading-5 text-[#222222] min-h-[44px] md:min-h-0 touch-manipulation"
                >
                  <input
                    type="radio"
                    name="typeOfMark"
                    value={opt}
                    checked={typeOfMark === opt}
                    onChange={() => { setTypeOfMark(opt); clearError("typeOfMark"); }}
                    className="w-4 h-4 accent-[#222222] cursor-pointer"
                  />
                  {opt}
                </label>
              ))}
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <label className={LABEL_CLASS}>Goods & Services{REQ}</label>
            <textarea
              value={goodsServices}
              onChange={(e) => { setGoodsServices(e.target.value); clearError("goodsServices"); }}
              placeholder="Text Here"
              className={TEXTAREA_CLASS + eb("goodsServices")}
              rows={2}
            />
          </div>
        </div>

        {/* Image of Our Mark — optional */}
        <div className="flex flex-col gap-2">
          <label className={LABEL_CLASS}>Image of Our Mark</label>
          <ImageUploadZone
            previewUrl={localImageOfMarkUrl}
            savedFileKey={savedImageOfMarkKey}
            saving={saving}
            inputId="our-mark-image-input"
            onFileSelected={handleImageOfMarkFile}
            onRemove={() => {
              if (localImageOfMarkUrl) URL.revokeObjectURL(localImageOfMarkUrl);
              setPendingImageOfMarkFile(null);
              setLocalImageOfMarkUrl(null);
              setSavedImageOfMarkKey(null);
            }}
            onPreview={(url, name) =>
              setPreview({ url, name, kind: detectPreviewKind(name), revokeOnClose: false })
            }
          />
        </div>
      </div>

      {/* Applicant Details */}
      <div className="flex flex-col gap-5 bg-white border border-[#EEEEEE] rounded-[10px] p-5">
        <p className="text-[14px] font-semibold text-[#101111]">Applicant Details</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="flex flex-col gap-1">
            <label className={LABEL_CLASS}>Applicant Name{REQ}</label>
            <input
              type="text"
              value={applicant}
              onChange={(e) => { setApplicant(e.target.value); clearError("applicant"); }}
              placeholder="Text Here"
              className={INPUT_CLASS + eb("applicant")}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className={LABEL_CLASS}>Applicant Address Line{REQ}</label>
            <input
              type="text"
              value={applicantAddressLine}
              onChange={(e) => { setApplicantAddressLine(e.target.value); clearError("applicantAddressLine"); }}
              placeholder="Text Here"
              className={INPUT_CLASS + eb("applicantAddressLine")}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className={LABEL_CLASS}>Applicant District{REQ}</label>
            <Select
              value={applicantAddressDistrict}
              onValueChange={(v) => { setApplicantAddressDistrict(v); clearError("applicantAddressDistrict"); }}
            >
              <SelectTrigger className={INPUT_CLASS + eb("applicantAddressDistrict")}>
                <SelectValue placeholder="Select District" />
              </SelectTrigger>
              <SelectContent>
                {OPPOSITION_KERALA_DISTRICTS.map((d) => (
                  <SelectItem key={d} value={d}>{d}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1">
            <label className={LABEL_CLASS}>Applicant State{REQ}</label>
            <input
              type="text"
              value={applicantAddressState}
              onChange={(e) => { setApplicantAddressState(e.target.value); clearError("applicantAddressState"); }}
              placeholder="Text Here"
              className={INPUT_CLASS + eb("applicantAddressState")}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className={LABEL_CLASS}>Applicant Country{REQ}</label>
            <input
              type="text"
              value={applicantAddressCountry}
              onChange={(e) => { setApplicantAddressCountry(e.target.value); clearError("applicantAddressCountry"); }}
              placeholder="Text Here"
              className={INPUT_CLASS + eb("applicantAddressCountry")}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className={LABEL_CLASS}>Applicant Type{REQ}</label>
            <Select
              value={applicantType}
              onValueChange={(v) => { setApplicantType(v); clearError("applicantType"); }}
            >
              <SelectTrigger className={INPUT_CLASS + eb("applicantType")}>
                <SelectValue placeholder="Select Type" />
              </SelectTrigger>
              <SelectContent>
                {OPPOSITION_APPLICANT_TYPE_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {applicantType === "OTHERS" && (
            <div className="flex flex-col gap-1">
              <label className={LABEL_CLASS}>Applicant Type (Other){REQ}</label>
              <input
                type="text"
                value={applicantTypeOther}
                onChange={(e) => { setApplicantTypeOther(e.target.value); clearError("applicantTypeOther"); }}
                placeholder="Specify type"
                className={INPUT_CLASS + eb("applicantTypeOther")}
              />
            </div>
          )}
          <div className="flex flex-col gap-1">
            <label className={LABEL_CLASS}>Signatory Name{REQ}</label>
            <input
              type="text"
              value={signatoryName}
              onChange={(e) => { setSignatoryName(e.target.value); clearError("signatoryName"); }}
              placeholder="Text Here"
              className={INPUT_CLASS + eb("signatoryName")}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className={LABEL_CLASS}>Signatory Designation{REQ}</label>
            <input
              type="text"
              value={signatoryDesignation}
              onChange={(e) => { setSignatoryDesignation(e.target.value); clearError("signatoryDesignation"); }}
              placeholder="Text Here"
              className={INPUT_CLASS + eb("signatoryDesignation")}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className={LABEL_CLASS}>User Date{REQ}</label>
            <DateTimePicker
              value={userDate}
              onChange={(d) => { setUserDate(d); clearError("userDate"); }}
              placeholder="Select Date"
              buttonClassName="px-[10px] py-2 rounded-[6px] text-[12px]"
              error={hasError("userDate")}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className={LABEL_CLASS}>Date of Filing{REQ}</label>
            <DateTimePicker
              value={dateOfFiling}
              onChange={(d) => { setDateOfFiling(d); clearError("dateOfFiling"); }}
              placeholder="Select Date"
              buttonClassName="px-[10px] py-2 rounded-[6px] text-[12px]"
              error={hasError("dateOfFiling")}
            />
          </div>
        </div>
      </div>

      {/* Addressee */}
      <div className="flex flex-col gap-5 bg-white border border-[#EEEEEE] rounded-[10px] p-5">
        <p className="text-[14px] font-semibold text-[#101111]">Addressee</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="flex flex-col gap-1">
            <label className={LABEL_CLASS}>Addressee Designation{REQ}</label>
            <Select
              value={addresseeDesignation}
              onValueChange={(v) => { setAddresseeDesignation(v); clearError("addresseeDesignation"); }}
            >
              <SelectTrigger className={INPUT_CLASS + eb("addresseeDesignation")}>
                <SelectValue placeholder="Select Designation" />
              </SelectTrigger>
              <SelectContent>
                {OPPOSITION_ADDRESSEE_DESIGNATIONS.map((d) => (
                  <SelectItem key={d} value={d}>{d === "NAME" ? "Name (Custom)" : d}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {addresseeDesignation === "NAME" && (
            <div className="flex flex-col gap-1">
              <label className={LABEL_CLASS}>Addressee Name{REQ}</label>
              <input
                type="text"
                value={addresseeCustomName}
                onChange={(e) => { setAddresseeCustomName(e.target.value); clearError("addresseeCustomName"); }}
                placeholder="Text Here"
                className={INPUT_CLASS + eb("addresseeCustomName")}
              />
            </div>
          )}
          <div className="flex flex-col gap-1">
            <label className={LABEL_CLASS}>Firm Name{REQ}</label>
            <input
              type="text"
              value={firmName}
              onChange={(e) => { setFirmName(e.target.value); clearError("firmName"); }}
              placeholder="Text Here"
              className={INPUT_CLASS + eb("firmName")}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className={LABEL_CLASS}>Place{REQ}</label>
            <input
              type="text"
              value={place}
              onChange={(e) => { setPlace(e.target.value); clearError("place"); }}
              placeholder="Text Here"
              className={INPUT_CLASS + eb("place")}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className={LABEL_CLASS}>Form of Address{REQ}</label>
            <Select
              value={addresseeHonorific}
              onValueChange={(v) => { setAddresseeHonorific(v); clearError("addresseeHonorific"); }}
            >
              <SelectTrigger className={INPUT_CLASS + eb("addresseeHonorific")}>
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                {OPPOSITION_HONORIFIC_OPTIONS.map((h) => (
                  <SelectItem key={h} value={h}>{h}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Conflicting Marks */}
      <div className="flex flex-col gap-5 bg-white border border-[#EEEEEE] rounded-[10px] p-5">
        <div className="flex items-center justify-between">
          <p className="text-[14px] font-semibold text-[#101111]">
            Conflicting Marks
            <span className="ml-2 text-[12px] font-normal text-[#6D6D6D]">({conflictingMarks.length})</span>
          </p>
          <button
            type="button"
            onClick={addConflictingMark}
            disabled={saving}
            className="flex items-center gap-1.5 text-[12px] font-medium text-[#222] border border-[#EEEEEE] rounded-[6px] px-3 py-1.5 hover:bg-[#F7F7F7] disabled:opacity-50"
          >
            <HiOutlinePlus className="w-3.5 h-3.5" strokeWidth={2} />
            Add Conflicting Mark
          </button>
        </div>

        <div className="flex flex-col gap-5">
          {conflictingMarks.map((row, idx) => (
            <ConflictingMarkCard
              key={row.rowId}
              row={row}
              index={idx}
              saving={saving}
              errors={errors}
              onUpdate={(updates) => updateRow(row.rowId, updates)}
              onRemove={() => removeConflictingMark(row.rowId)}
              onImageFile={(file) => handleConflictingMarkImageFile(row.rowId, file)}
              onStatusPdfFile={(file) => handleConflictingMarkStatusPdf(row.rowId, file)}
              onClearError={(field) => clearError(`${row.rowId}:${field}`)}
              onPreview={(url, name, kind, revoke) =>
                setPreview({ url, name, kind, revokeOnClose: revoke })
              }
            />
          ))}
        </div>
      </div>

      <FilePreviewModal
        open={!!preview}
        onClose={() => {
          if (preview?.revokeOnClose) URL.revokeObjectURL(preview.url);
          setPreview(null);
        }}
        name={preview?.name ?? ""}
        url={preview?.url}
        kind={preview?.kind}
      />
    </form>
  );
}

// ---------------------------------------------------------------------------
// ConflictingMarkCard
// ---------------------------------------------------------------------------

type ConflictingMarkCardProps = {
  row: ConflictingMarkRow;
  index: number;
  saving: boolean;
  errors: Set<string>;
  onUpdate: (updates: Partial<ConflictingMarkRow>) => void;
  onRemove: () => void;
  onImageFile: (file: File) => void;
  onStatusPdfFile: (file: File) => void;
  onClearError: (field: string) => void;
  onPreview: (url: string, name: string, kind: FilePreviewKind, revoke: boolean) => void;
};

function ConflictingMarkCard({
  row,
  index,
  saving,
  errors,
  onUpdate,
  onRemove,
  onImageFile,
  onStatusPdfFile,
  onClearError,
  onPreview,
}: ConflictingMarkCardProps) {
  const imageInputId = `cm-image-input-${row.rowId}`;
  const pdfInputId = `cm-pdf-input-${row.rowId}`;
  const [imageDragging, setImageDragging] = useState(false);
  const [pdfDragging, setPdfDragging] = useState(false);

  const re = (field: string) => errors.has(`${row.rowId}:${field}`);
  const reb = (field: string) => (re(field) ? " !border-red-500" : "");

  const handleJournalDateChange = (date: Date | null) => {
    onUpdate({ journalDate: date, lastDateOfFiling: date ? addTwoMonths(date) : null });
    onClearError("journalDate");
    onClearError("lastDateOfFiling");
  };

  const imagePreviewSrc = row.localImagePreviewUrl;
  const hasImage = !!(row.localImagePreviewUrl ?? row.savedImageFileKey);
  const hasStatusPdf = !!(row.pendingStatusFile ?? row.savedStatusFileKey);
  const statusFilename = row.pendingStatusFile?.name ?? row.savedStatusFilename ?? null;

  return (
    <div className="flex flex-col gap-4 border border-[#EEEEEE] rounded-[10px] p-4 bg-[#FAFAFA]">
      {/* Header */}
      <div className="flex items-center justify-between">
        <span className="text-[13px] font-semibold text-[#222]">Conflicting Mark #{index + 1}</span>
        <button
          type="button"
          onClick={onRemove}
          disabled={saving}
          className="text-[#E53E3E] hover:text-red-700 disabled:opacity-50"
          title="Remove"
        >
          <HiOutlineTrash className="w-4 h-4" strokeWidth={1.5} />
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="flex flex-col gap-1">
          <label className={LABEL_CLASS}>Name of Proprietor{REQ}</label>
          <input
            type="text"
            value={row.nameOfProprietor}
            onChange={(e) => { onUpdate({ nameOfProprietor: e.target.value }); onClearError("nameOfProprietor"); }}
            placeholder="Text Here"
            className={INPUT_CLASS + reb("nameOfProprietor")}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className={LABEL_CLASS}>Address of Proprietor{REQ}</label>
          <input
            type="text"
            value={row.addressOfProprietor}
            onChange={(e) => { onUpdate({ addressOfProprietor: e.target.value }); onClearError("addressOfProprietor"); }}
            placeholder="Text Here"
            className={INPUT_CLASS + reb("addressOfProprietor")}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className={LABEL_CLASS}>Place of Proprietor{REQ}</label>
          <input
            type="text"
            value={row.placeOfProprietor}
            onChange={(e) => { onUpdate({ placeOfProprietor: e.target.value }); onClearError("placeOfProprietor"); }}
            placeholder="Text Here"
            className={INPUT_CLASS + reb("placeOfProprietor")}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className={LABEL_CLASS}>Conflicting Mark{REQ}</label>
          <input
            type="text"
            value={row.conflictingMark}
            onChange={(e) => { onUpdate({ conflictingMark: e.target.value }); onClearError("conflictingMark"); }}
            placeholder="Text Here"
            className={INPUT_CLASS + reb("conflictingMark")}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className={LABEL_CLASS}>Application Number{REQ}</label>
          <input
            type="text"
            value={row.applicationNo}
            onChange={(e) => { onUpdate({ applicationNo: e.target.value }); onClearError("applicationNo"); }}
            placeholder="Text Here"
            className={INPUT_CLASS + reb("applicationNo")}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className={LABEL_CLASS}>Class of Mark{REQ}</label>
          <Select
            value={row.classOfMark}
            onValueChange={(v) => { onUpdate({ classOfMark: v }); onClearError("classOfMark"); }}
          >
            <SelectTrigger className={INPUT_CLASS + reb("classOfMark")}>
              <SelectValue placeholder="Select Class" />
            </SelectTrigger>
            <SelectContent>
              {OPPOSITION_CLASS_NUMBERS.map((n) => (
                <SelectItem key={n} value={String(n)}>{n}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1 md:col-span-2">
          <label className={LABEL_CLASS}>Goods & Services{REQ}</label>
          <textarea
            value={row.goodsServices}
            onChange={(e) => { onUpdate({ goodsServices: e.target.value }); onClearError("goodsServices"); }}
            placeholder="Text Here"
            className={TEXTAREA_CLASS + reb("goodsServices")}
            rows={2}
          />
        </div>
      </div>

      {/* Image of Conflicting Mark — required */}
      <div className="flex flex-col gap-2">
        <label className={LABEL_CLASS}>Image of Conflicting Mark</label>
        {imagePreviewSrc ? (
          <div className="flex flex-col gap-2">
            <img
              src={imagePreviewSrc}
              alt="Conflicting mark"
              className="w-full max-h-[200px] object-contain rounded-[10px] border border-[#EEEEEE] bg-white"
            />
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => onPreview(imagePreviewSrc, "Conflicting Mark Image", "image", false)}
                className="px-3 h-8 rounded-[8px] border border-[#D5D3E0] bg-white text-xs font-medium text-[#2E3037] hover:bg-gray-50"
              >
                Preview
              </button>
              <button
                type="button"
                disabled={saving}
                onClick={() => document.getElementById(imageInputId)?.click()}
                className="px-3 h-8 rounded-[8px] border border-[#D5D3E0] bg-white text-xs font-medium text-[#2E3037] hover:bg-gray-50 disabled:opacity-50"
              >
                Replace
              </button>
              <button
                type="button"
                disabled={saving}
                onClick={() => { onUpdate({ pendingImageFile: null, localImagePreviewUrl: null, savedImageFileKey: null }); }}
                className="text-[#6D6D6D] hover:text-red-600 p-1 disabled:opacity-50"
              >
                <HiOutlineX className="w-4 h-4" strokeWidth={2} />
              </button>
            </div>
          </div>
        ) : hasImage ? (
          <div className="flex items-center gap-2 px-3 py-2 rounded-[8px] border border-[#EEE] bg-[#FCFCFC]">
            <span className="text-xs text-[#222] truncate flex-1">
              {row.savedImageFileKey?.split("/").pop() ?? "Uploaded image"}
            </span>
            <button
              type="button"
              disabled={saving}
              onClick={() => document.getElementById(imageInputId)?.click()}
              className="px-3 h-8 rounded-[8px] border border-[#D5D3E0] bg-white text-xs font-medium text-[#2E3037] hover:bg-gray-50 disabled:opacity-50"
            >
              Replace
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={() => onUpdate({ savedImageFileKey: null })}
              className="text-[#6D6D6D] hover:text-red-600 p-1 disabled:opacity-50"
            >
              <HiOutlineX className="w-4 h-4" strokeWidth={2} />
            </button>
          </div>
        ) : (
          <div
            className={`flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 px-5 py-4 rounded-[16px] border border-dashed bg-white transition-colors cursor-pointer ${
              imageDragging ? "border-[#222] bg-[#F5F5F5]" : "border-[#D8DAEB]"
            }`}
            onDragOver={(e) => { e.preventDefault(); setImageDragging(true); }}
            onDragLeave={() => setImageDragging(false)}
            onDrop={(e) => {
              e.preventDefault();
              setImageDragging(false);
              const file = e.dataTransfer.files?.[0];
              if (file) onImageFile(file);
            }}
            onClick={() => document.getElementById(imageInputId)?.click()}
          >
            <div className="flex items-center gap-4 min-w-0">
              <img src={uploadDropzoneGraphic} alt="" width={40} height={40} className="flex-shrink-0" />
              <div className="flex flex-col gap-1">
                <p className="text-[13px] font-medium text-[#101111]">
                  Drag and drop image
                </p>
                <p className="text-[11px] text-[#7D7D7D]">JPEG, PNG, WEBP, up to 50MB</p>
              </div>
            </div>
            <button
              type="button"
              disabled={saving}
              onClick={(e) => { e.stopPropagation(); document.getElementById(imageInputId)?.click(); }}
              className="flex-shrink-0 flex items-center justify-center h-9 px-4 rounded-[10px] bg-white border border-[#D5D3E0] text-[13px] font-medium text-[#2E3037] hover:bg-gray-50 disabled:opacity-60 self-start sm:self-auto"
            >
              Select File
            </button>
          </div>
        )}
        <input
          id={imageInputId}
          type="file"
          accept=".jpg,.jpeg,.png,.webp"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) onImageFile(file);
            e.currentTarget.value = "";
          }}
        />
      </div>

      {/* Dates */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="flex flex-col gap-1">
          <label className={LABEL_CLASS}>User Date{REQ}</label>
          <DateTimePicker
            value={row.userDate}
            onChange={(d) => { onUpdate({ userDate: d }); onClearError("userDate"); }}
            placeholder="Select Date"
            buttonClassName="px-[10px] py-2 rounded-[6px] text-[12px]"
            error={re("userDate")}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className={LABEL_CLASS}>Journal Date{REQ}</label>
          <DateTimePicker
            value={row.journalDate}
            onChange={handleJournalDateChange}
            placeholder="Select Date"
            buttonClassName="px-[10px] py-2 rounded-[6px] text-[12px]"
            error={re("journalDate")}
          />
        </div>
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-1">
          <label className={LABEL_CLASS}>Last Date for Filing Opposition{REQ}</label>
          <span className="relative inline-flex items-center group">
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" className="cursor-help flex-shrink-0">
              <circle cx="8" cy="8" r="6.5" stroke="#666666" strokeWidth="1.25" />
              <path d="M8 7.5 V11" stroke="#666666" strokeWidth="1.25" strokeLinecap="round" />
              <circle cx="8" cy="5.33" r="0.6" fill="#666666" />
            </svg>
            <span role="tooltip" className="hidden group-hover:flex absolute z-20 left-0 top-[20px] rounded-[6px] text-white pointer-events-none" style={{ width: "240px", maxWidth: "calc(100vw - 40px)", padding: "6px 10px", background: "#565656", fontSize: "11px", lineHeight: "18px", fontWeight: 400 }}>
              Auto-calculated as Journal Date + 2 months. You can override it manually.
            </span>
          </span>
        </div>
          <DateTimePicker
            value={row.lastDateOfFiling}
            onChange={(d) => { onUpdate({ lastDateOfFiling: d }); onClearError("lastDateOfFiling"); }}
            placeholder="Auto-calculated"
            buttonClassName="px-[10px] py-2 rounded-[6px] text-[12px]"
            error={re("lastDateOfFiling")}
          />
        </div>
      </div>

      {/* Status of Conflicting Mark PDF — required */}
      <div className="flex flex-col gap-2">
        <label className={LABEL_CLASS}>Status of Conflicting Mark{REQ}</label>
        {hasStatusPdf && statusFilename ? (
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-3 py-2 rounded-[8px] border border-[#EEE] bg-[#FCFCFC]">
            <span className="text-xs text-[#222] truncate flex-1">{statusFilename}</span>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                type="button"
                disabled={saving}
                onClick={() => document.getElementById(pdfInputId)?.click()}
                className="px-3 h-8 rounded-[8px] border border-[#D5D3E0] bg-white text-xs font-medium text-[#2E3037] hover:bg-gray-50 disabled:opacity-50"
              >
                Replace
              </button>
              <button
                type="button"
                disabled={saving}
                onClick={() => onUpdate({ pendingStatusFile: null, savedStatusFileKey: null, savedStatusFilename: null })}
                className="text-[#6D6D6D] hover:text-red-600 p-1 disabled:opacity-50"
              >
                <HiOutlineX className="w-4 h-4" strokeWidth={2} />
              </button>
            </div>
          </div>
        ) : (
          <div
            className={`flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 px-5 py-4 rounded-[16px] border border-dashed bg-white transition-colors cursor-pointer ${
              re("statusOfConflictingMark")
                ? "border-red-500"
                : pdfDragging
                ? "border-[#222] bg-[#F5F5F5]"
                : "border-[#D8DAEB]"
            }`}
            onDragOver={(e) => { e.preventDefault(); setPdfDragging(true); }}
            onDragLeave={() => setPdfDragging(false)}
            onDrop={(e) => {
              e.preventDefault();
              setPdfDragging(false);
              const file = e.dataTransfer.files?.[0];
              if (file) onStatusPdfFile(file);
            }}
            onClick={() => document.getElementById(pdfInputId)?.click()}
          >
            <div className="flex items-center gap-4 min-w-0">
              <img src={uploadDropzoneGraphic} alt="" width={40} height={40} className="flex-shrink-0" />
              <div className="flex flex-col gap-1">
                <p className="text-[13px] font-medium text-[#101111]">Upload Status PDF</p>
                <p className="text-[11px] text-[#7D7D7D]">PDF format, up to 50MB</p>
              </div>
            </div>
            <button
              type="button"
              disabled={saving}
              onClick={(e) => { e.stopPropagation(); document.getElementById(pdfInputId)?.click(); }}
              className="flex-shrink-0 flex items-center justify-center h-9 px-4 rounded-[10px] bg-white border border-[#D5D3E0] text-[13px] font-medium text-[#2E3037] hover:bg-gray-50 disabled:opacity-60 self-start sm:self-auto"
            >
              Select File
            </button>
          </div>
        )}
        <input
          id={pdfInputId}
          type="file"
          accept=".pdf"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) onStatusPdfFile(file);
            e.currentTarget.value = "";
          }}
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ImageUploadZone
// ---------------------------------------------------------------------------

type ImageUploadZoneProps = {
  previewUrl: string | null;
  savedFileKey: string | null;
  saving: boolean;
  inputId: string;
  onFileSelected: (file: File) => void;
  onRemove: () => void;
  onPreview: (url: string, name: string) => void;
};

function ImageUploadZone({ previewUrl, savedFileKey, saving, inputId, onFileSelected, onRemove, onPreview }: ImageUploadZoneProps) {
  const [dragging, setDragging] = useState(false);
  const hasImage = !!(previewUrl ?? savedFileKey);

  return (
    <div className="flex flex-col gap-2">
      {previewUrl ? (
        <div className="flex flex-col gap-2">
          <img
            src={previewUrl}
            alt="Our mark"
            className="w-full max-h-[200px] object-contain rounded-[10px] border border-[#EEEEEE] bg-white"
          />
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => onPreview(previewUrl, "Our Mark Image")}
              className="px-3 h-8 rounded-[8px] border border-[#D5D3E0] bg-white text-xs font-medium text-[#2E3037] hover:bg-gray-50"
            >
              Preview
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={() => document.getElementById(inputId)?.click()}
              className="px-3 h-8 rounded-[8px] border border-[#D5D3E0] bg-white text-xs font-medium text-[#2E3037] hover:bg-gray-50 disabled:opacity-50"
            >
              Replace
            </button>
            <button type="button" disabled={saving} onClick={onRemove} className="text-[#6D6D6D] hover:text-red-600 p-1 disabled:opacity-50">
              <HiOutlineX className="w-4 h-4" strokeWidth={2} />
            </button>
          </div>
        </div>
      ) : hasImage ? (
        <div className="flex items-center gap-2 px-3 py-2 rounded-[8px] border border-[#EEE] bg-[#FCFCFC]">
          <span className="text-xs text-[#222] truncate flex-1">{savedFileKey?.split("/").pop() ?? "Uploaded image"}</span>
          <button
            type="button"
            disabled={saving}
            onClick={() => document.getElementById(inputId)?.click()}
            className="px-3 h-8 rounded-[8px] border border-[#D5D3E0] bg-white text-xs font-medium text-[#2E3037] hover:bg-gray-50 disabled:opacity-50"
          >
            Replace
          </button>
          <button type="button" disabled={saving} onClick={onRemove} className="text-[#6D6D6D] hover:text-red-600 p-1 disabled:opacity-50">
            <HiOutlineX className="w-4 h-4" strokeWidth={2} />
          </button>
        </div>
      ) : (
        <div
          className={`flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 px-5 py-4 rounded-[16px] border border-dashed bg-white transition-colors cursor-pointer ${
            dragging ? "border-[#222] bg-[#F5F5F5]" : "border-[#D8DAEB]"
          }`}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            const file = e.dataTransfer.files?.[0];
            if (file) onFileSelected(file);
          }}
          onClick={() => document.getElementById(inputId)?.click()}
        >
          <div className="flex items-center gap-4 min-w-0">
            <img src={uploadDropzoneGraphic} alt="" width={40} height={40} className="flex-shrink-0" />
            <div className="flex flex-col gap-1">
              <p className="text-[13px] font-medium text-[#101111]">Drag and drop image</p>
              <p className="text-[11px] text-[#7D7D7D]">JPEG, PNG, WEBP, up to 50MB</p>
            </div>
          </div>
          <button
            type="button"
            disabled={saving}
            onClick={(e) => { e.stopPropagation(); document.getElementById(inputId)?.click(); }}
            className="flex-shrink-0 flex items-center justify-center h-9 px-4 rounded-[10px] bg-white border border-[#D5D3E0] text-[13px] font-medium text-[#2E3037] hover:bg-gray-50 disabled:opacity-60 self-start sm:self-auto"
          >
            Select File
          </button>
        </div>
      )}
      <input
        id={inputId}
        type="file"
        accept=".jpg,.jpeg,.png,.webp"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) onFileSelected(file);
          e.currentTarget.value = "";
        }}
      />
    </div>
  );
}
